import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import '../styles/MyRegistrations.css';

export function MyRegistrations() {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedReg, setSelectedReg] = useState(null);
  const [showQRModal, setShowQRModal] = useState(false);
  const [editingReg, setEditingReg] = useState(null);
  const [editFormData, setEditFormData] = useState({});

  useEffect(() => {
    fetchUserRegistrations();
  }, []);

  const fetchUserRegistrations = async () => {
    try {
      setLoading(true);
      setError('');
      const sessionToken = localStorage.getItem('session_token');
      const userId = localStorage.getItem('user_id');

      if (!userId) {
        setError('User ID not found. Please log in again.');
        setLoading(false);
        return;
      }

      const response = await fetch(`/api/user-registrations?user_id=${encodeURIComponent(userId)}`, {
        headers: {
          'X-Session-Token': sessionToken,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch registrations');
      }

      const data = await response.json();
      setRegistrations(data.registrations || []);
    } catch (err) {
      setError(err.message || 'Failed to load registrations');
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (reg) => {
    setEditingReg(reg.id);
    setEditFormData({
      first_name: reg.first_name,
      last_name: reg.last_name,
      whatsapp_number: reg.whatsapp_number,
      attendance_status: reg.attendance_status,
      comments: reg.comments || '',
    });
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleUpdateRegistration = async (regId) => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      
      const response = await fetch(`/api/registrations/${regId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-Token': sessionToken,
        },
        body: JSON.stringify(editFormData),
      });

      if (!response.ok) {
        throw new Error('Failed to update registration');
      }

      setEditingReg(null);
      await fetchUserRegistrations();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCancelRegistration = async (regId) => {
    if (!window.confirm('Are you sure you want to cancel this registration?')) {
      return;
    }

    try {
      const sessionToken = localStorage.getItem('session_token');
      
      const response = await fetch(`/api/registrations/${regId}`, {
        method: 'DELETE',
        headers: {
          'X-Session-Token': sessionToken,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to cancel registration');
      }

      await fetchUserRegistrations();
      setSelectedReg(null);
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) {
    return (
      <div className="card">
        <div className="card-header bg-primary text-white">
          <h5 className="mb-0">🎫 My Registrations</h5>
        </div>
        <div className="card-body text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card">
        <div className="card-header bg-danger text-white">
          <h5 className="mb-0">🎫 My Registrations</h5>
        </div>
        <div className="card-body">
          <div className="alert alert-danger mb-0">{error}</div>
        </div>
      </div>
    );
  }

  if (registrations.length === 0) {
    return (
      <div className="card">
        <div className="card-header bg-primary text-white">
          <h5 className="mb-0">🎫 My Registrations</h5>
        </div>
        <div className="card-body">
          <p className="text-muted mb-0">No registrations yet. Register for an event to see your tickets here!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header bg-primary text-white no-print">
        <h5 className="mb-0">🎫 My Registrations ({registrations.length})</h5>
      </div>
      <div className="card-body no-print">
        <div className="registrations-grid">
          {registrations.map((reg) => (
            <div key={reg.id} className="registration-card">
              <div className="reg-header">
                <h6 className="mb-2">{reg.event_name}</h6>
                <span className={`badge bg-${reg.attendance_status === 'yes' ? 'success' : reg.attendance_status === 'no' ? 'danger' : 'warning'}`}>
                  {reg.attendance_status}
                </span>
              </div>

              {editingReg === reg.id ? (
                // Edit Mode
                <div className="edit-form">
                  <div className="mb-3">
                    <label className="form-label">First Name</label>
                    <input
                      type="text"
                      className="form-control"
                      name="first_name"
                      value={editFormData.first_name}
                      onChange={handleEditChange}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Last Name</label>
                    <input
                      type="text"
                      className="form-control"
                      name="last_name"
                      value={editFormData.last_name}
                      onChange={handleEditChange}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">WhatsApp Number</label>
                    <input
                      type="tel"
                      className="form-control"
                      name="whatsapp_number"
                      value={editFormData.whatsapp_number}
                      onChange={handleEditChange}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Attendance Status</label>
                    <select
                      className="form-control"
                      name="attendance_status"
                      value={editFormData.attendance_status}
                      onChange={handleEditChange}
                    >
                      <option value="yes">Yes, I'll attend</option>
                      <option value="no">No, I can't attend</option>
                      <option value="maybe">Maybe</option>
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Comments</label>
                    <textarea
                      className="form-control"
                      name="comments"
                      value={editFormData.comments}
                      onChange={handleEditChange}
                      rows="2"
                    />
                  </div>
                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-sm btn-success"
                      onClick={() => handleUpdateRegistration(reg.id)}
                    >
                      Save
                    </button>
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() => setEditingReg(null)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                // View Mode
                <>
                  <div className="reg-details">
                    <div className="detail-row">
                      <span className="label">Name:</span>
                      <span className="value">{reg.first_name} {reg.last_name}</span>
                    </div>
                    <div className="detail-row">
                      <span className="label">Email:</span>
                      <span className="value">{reg.email}</span>
                    </div>
                    <div className="detail-row">
                      <span className="label">WhatsApp:</span>
                      <span className="value">{reg.whatsapp_number}</span>
                    </div>
                    {reg.comments && (
                      <div className="detail-row">
                        <span className="label">Notes:</span>
                        <span className="value">{reg.comments}</span>
                      </div>
                    )}
                    <div className="detail-row">
                      <span className="label">Registered:</span>
                      <span className="value">{new Date(reg.registration_date).toLocaleDateString()}</span>
                    </div>
                  </div>

                  <div className="reg-actions">
                    {reg.verify_ticket_number === 1 ? (
                      <button
                        className="btn btn-sm btn-info"
                        onClick={() => {
                          setSelectedReg(reg);
                          setShowQRModal(true);
                        }}
                        title="View Ticket & QR Code"
                      >
                        🎟️ Ticket
                      </button>
                    ) : (
                      <button
                        className="btn btn-sm btn-secondary"
                        disabled
                        title="Waiting for Admin Approval"
                      >
                        ⏳ Pending Approval
                      </button>
                    )}
                    <button
                      className="btn btn-sm btn-warning"
                      onClick={() => handleEditClick(reg)}
                      title="Edit Registration"
                    >
                      ✏️ Edit
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleCancelRegistration(reg.id)}
                      title="Cancel Registration"
                    >
                      ❌ Cancel
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* QR Code Modal */}
      {showQRModal && selectedReg && createPortal(
        <div 
          className="modal d-block" 
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowQRModal(false);
          }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Ticket & QR Code</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowQRModal(false)}
                />
              </div>
              <div className="modal-body text-center">
                <div className="ticket-display">
                  <h6 className="mb-3">{selectedReg.event_name}</h6>
                  
                  <div className="mb-4">
                    <p className="text-muted small mb-2">Ticket Number:</p>
                    <h4 className="ticket-number">{selectedReg.ticket_number}</h4>
                  </div>

                  <div className="qr-code-container mb-4">
                    <img
                      src={selectedReg.qr_code}
                      alt="QR Code"
                      className="qr-code-image"
                    />
                  </div>

                  <div className="ticket-info">
                    <p><strong>{selectedReg.first_name} {selectedReg.last_name}</strong></p>
                    <p className="text-muted small">{selectedReg.email}</p>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    const printWindow = window.open('', '_blank');
                    printWindow.document.write(`
                      <html>
                        <head>
                          <title>Print Ticket</title>
                          <style>
                            body { 
                              font-family: Arial, sans-serif; 
                              padding: 40px;
                              display: flex;
                              justify-content: center;
                            }
                            .ticket-print {
                              border: 4px solid #667eea;
                              border-radius: 20px;
                              padding: 50px;
                              max-width: 600px;
                              width: 100%;
                              text-align: center;
                              -webkit-print-color-adjust: exact;
                              print-color-adjust: exact;
                            }
                            h1 { font-size: 32px; color: #222; margin-bottom: 30px; }
                            .tkt-label { color: #666; font-size: 18px; margin-bottom: 10px; }
                            .tkt-num { font-family: monospace; font-size: 40px; font-weight: bold; margin: 0 0 40px 0; letter-spacing: 2px; }
                            .qr-container { padding: 30px; margin: 0 auto 40px auto; }
                            .qr-container img { max-width: 350px; }
                            .name { font-size: 28px; font-weight: bold; margin-bottom: 10px; }
                            .email { font-size: 20px; color: #555; }
                          </style>
                        </head>
                        <body>
                          <div class="ticket-print">
                            <h1>${selectedReg.event_name}</h1>
                            <div class="tkt-label">Ticket Number:</div>
                            <div class="tkt-num">${selectedReg.ticket_number}</div>
                            <div class="qr-container">
                              <img src="${selectedReg.qr_code}" alt="QR Code" />
                            </div>
                            <div class="name">${selectedReg.first_name} ${selectedReg.last_name}</div>
                            <div class="email">${selectedReg.email}</div>
                          </div>
                          <script>
                            setTimeout(() => {
                              window.print();
                              window.close();
                            }, 500);
                          </script>
                        </body>
                      </html>
                    `);
                    printWindow.document.close();
                  }}
                >
                  🖨️ Print
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowQRModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
